import {
  useAccountBalancesQuery,
  useAllAccountsQuery,
  useAllPlaidAccessTokensQuery,
  useRecentSyncsQuery,
  type AccountListItemFragment,
  type FullPlaidAccessTokenFragment,
} from '@transactions/graphql';
import {Plus} from 'lucide-react';
import * as React from 'react';
import {useSearchParams} from 'react-router-dom';
import {indexBalances} from 'src/AccountsView/accountBalances';
import {
  ACCOUNT_TYPE_OPTIONS,
  isAccountClosed,
  isAccountInactive,
  matchesAccountFilter,
  matchesAccountTypeFilter,
  parseAccountFilter,
  parseAccountTypeFilter,
  parseExplicitAccountFilter,
  serializeAccountTypeFilter,
} from 'src/AccountsView/accountFilter';
import {
  ConnectionGroup,
  connectionTitle,
} from 'src/AccountsView/ConnectionGroup';
import {useBreadcrumb} from 'src/Breadcrumbs';
import {Button} from 'src/Button';
import {FilterDropdown} from 'src/components/FilterDropdown';
import {HeaderActions} from 'src/components/HeaderActions';
import {RecentSyncs} from 'src/ConnectionsView/RecentSyncs';
import {useModals} from 'src/Modals';
import type {
  AccountFilter,
  AccountTypeFilter,
} from 'src/AccountsView/accountFilter';
import type {ConnectionSync} from 'src/AccountsView/ConnectionGroup';

type GroupedAccounts = {
  token: FullPlaidAccessTokenFragment;
  accounts: AccountListItemFragment[];
}[];

export const ConnectionsView = () => {
  useBreadcrumb({
    label: 'Connections',
    fallback: [{label: 'Settings', href: '/settings'}],
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const typeFilter = parseAccountTypeFilter(searchParams.get('type'));
  // Same contextual default as the Accounts page: picking a type is a
  // narrowing gesture, and hiding inactive/closed matches would make the
  // very accounts being asked for vanish — so the status default widens.
  // An explicit pick always wins.
  const explicitFilter = parseExplicitAccountFilter(searchParams.get('filter'));
  const defaultFilter: AccountFilter = typeFilter.length > 0 ? 'all' : 'active';
  const filter = explicitFilter ?? defaultFilter;

  const {
    data: accountsData,
    loading: accountsLoading,
    refetch: refetchAccounts,
  } = useAllAccountsQuery({fetchPolicy: 'cache-and-network'});
  const {
    data: tokensData,
    loading: tokensLoading,
    refetch: refetchTokens,
  } = useAllPlaidAccessTokensQuery({fetchPolicy: 'cache-and-network'});
  // Balances stream in after the metadata (they are derived server-side);
  // rows show a placeholder until they land.
  const {data: balancesData, refetch: refetchBalances} =
    useAccountBalancesQuery({fetchPolicy: 'cache-and-network'});
  // Same query (and cache entry) as the Recent syncs list below; each
  // connection's status line reads its latest result from it.
  const {data: syncsData} = useRecentSyncsQuery({
    variables: {limit: 5},
    fetchPolicy: 'cache-and-network',
  });
  const {connectPlaidModal} = useModals();

  const handleRefetch = React.useCallback(() => {
    refetchAccounts();
    refetchTokens();
    refetchBalances();
  }, [refetchAccounts, refetchTokens, refetchBalances]);

  const setFilter = (newFilter: AccountFilter) => {
    const next = new URLSearchParams(searchParams);
    // Only a non-default pick is real state (the default is contextual).
    if (newFilter === defaultFilter) {
      next.delete('filter');
    } else {
      next.set('filter', newFilter);
    }
    setSearchParams(next);
  };

  const setTypeFilter = (newTypeFilter: AccountTypeFilter) => {
    const next = new URLSearchParams(searchParams);
    const serialized = serializeAccountTypeFilter(newTypeFilter);
    if (serialized == null) {
      next.delete('type');
    } else {
      next.set('type', serialized);
    }
    setSearchParams(next);
  };

  const handleConnect = () => {
    // linkPlaidAccount returns only a Boolean, so Apollo can't merge the
    // new token into the cached lists — refetch on success.
    connectPlaidModal.openModal({onSuccess: handleRefetch});
  };

  // cache-and-network reports loading during background refreshes — only
  // block on the initial fetch.
  if ((accountsLoading && !accountsData) || (tokensLoading && !tokensData)) {
    return (
      <div className="flex items-center justify-center p-8">
        <span className="text-muted-foreground">Loading connections...</span>
      </div>
    );
  }

  const balances = indexBalances(balancesData);
  // Contextual defaults don't count as narrowing, and neither does an
  // explicit All — only a pick that actually restricts the view prunes
  // groups from the page.
  const explicitlyNarrowed =
    (explicitFilter != null && explicitFilter !== 'all') ||
    typeFilter.length > 0;
  const allAccounts = accountsData?.allAccounts ?? [];
  const tokens = tokensData?.allPlaidAccessTokens ?? [];

  const matchesStatus = (
    statusFilter: AccountFilter,
    account: AccountListItemFragment,
  ) => matchesAccountFilter(statusFilter, account, balances.get(account._id));

  // Each dropdown's counts respect the OTHER filter (GitHub-style cross
  // counts), mirroring the Accounts page.
  const typeMatchedAccounts = allAccounts.filter((account) =>
    matchesAccountTypeFilter(typeFilter, account),
  );
  const statusMatchedAccounts = allAccounts.filter((account) =>
    matchesStatus(filter, account),
  );
  const visibleAccounts = typeMatchedAccounts.filter((account) =>
    matchesStatus(filter, account),
  );

  const statusOptions = [
    {
      key: 'active',
      label: 'Active',
      count: typeMatchedAccounts.filter((account) =>
        matchesStatus('active', account),
      ).length,
    },
    {
      key: 'inactive',
      label: 'Inactive',
      count: typeMatchedAccounts.filter((account) =>
        isAccountInactive(account, balances.get(account._id)),
      ).length,
    },
    {
      key: 'closed',
      label: 'Closed',
      count: typeMatchedAccounts.filter(isAccountClosed).length,
    },
  ];
  const typeOptions = ACCOUNT_TYPE_OPTIONS.map((option) => ({
    key: option.key,
    label: option.label,
    group: option.group,
    count: statusMatchedAccounts.filter(option.matches).length,
    present: allAccounts.some(option.matches),
  })).filter((option) => option.present || typeFilter.includes(option.key));

  // Group accounts by token.
  const grouped: GroupedAccounts = [];
  const accountsWithToken = new Set<string>();
  for (const token of tokens) {
    const accountIds = new Set(token.accounts.map((a) => a._id));
    const tokenAccounts = visibleAccounts.filter((account) =>
      accountIds.has(account._id),
    );
    // A connection with no linked accounts at all stays visible under the
    // default view — this page is the only place to edit, debug, or delete
    // it. Explicit narrowing still prunes it (an empty group is noise
    // then), and a connection whose accounts are merely filtered out stays
    // hidden as before: the status filter is the way back in.
    const accountless = token.accounts.length === 0;
    if (tokenAccounts.length > 0 || (accountless && !explicitlyNarrowed)) {
      grouped.push({token, accounts: tokenAccounts});
    }
    tokenAccounts.forEach((a) => accountsWithToken.add(a._id));
  }

  // Manual accounts (no token).
  const syntheticAccounts = visibleAccounts.filter(
    (account) => !accountsWithToken.has(account._id),
  );

  const hasNoConnections = tokens.length === 0 && syntheticAccounts.length === 0;

  // Newest first from the server, so the first result per token wins.
  const lastSyncByToken = new Map<string, ConnectionSync>();
  for (const sync of syncsData?.recentSyncs ?? []) {
    for (const result of sync.results) {
      if (!lastSyncByToken.has(result.tokenId)) {
        lastSyncByToken.set(result.tokenId, {startedAt: sync.startedAt, result});
      }
    }
  }

  const renderBody = () => {
    if (hasNoConnections) {
      return (
        <div className="border-border flex flex-col items-center gap-4 rounded-xl border border-dashed p-8">
          <p className="text-muted-foreground">No connections</p>
          <Button variant="outline" onClick={handleConnect} type="button">
            Connect with Plaid
          </Button>
        </div>
      );
    }

    if (grouped.length === 0 && syntheticAccounts.length === 0) {
      return (
        <div className="text-muted-foreground py-8 text-center text-sm">
          {typeFilter.length === 0
            ? `No ${filter} accounts`
            : 'No matching accounts'}
        </div>
      );
    }

    // Two connections to one bank are told apart by their id suffix.
    const titleCounts = new Map<string, number>();
    for (const {token} of grouped) {
      const title = connectionTitle(token);
      titleCounts.set(title, (titleCounts.get(title) ?? 0) + 1);
    }
    const renderGroup = ({
      token,
      accounts,
    }: GroupedAccounts[number]) => (
      <ConnectionGroup
        key={token._id}
        token={token}
        accounts={accounts}
        balances={balances}
        lastSync={lastSyncByToken.get(token._id)}
        showSuffix={(titleCounts.get(connectionTitle(token)) ?? 0) > 1}
        onRefetch={handleRefetch}
      />
    );

    // Disconnected connections sink below the manual accounts.
    return (
      <div className="flex flex-col gap-3">
        {grouped.filter(({token}) => token.active).map(renderGroup)}

        {syntheticAccounts.length > 0 && (
          <ConnectionGroup
            token={null}
            accounts={syntheticAccounts}
            balances={balances}
            onRefetch={handleRefetch}
          />
        )}

        {grouped.filter(({token}) => !token.active).map(renderGroup)}
      </div>
    );
  };

  return (
    <div className="flex flex-1 flex-col overflow-y-auto">
      <HeaderActions>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Add connection"
          onClick={handleConnect}
        >
          <Plus className="size-5" />
        </Button>
      </HeaderActions>
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 p-4 sm:px-6">
        {allAccounts.length > 0 && (
          <div className="flex items-center gap-2">
            <FilterDropdown
              label="Status"
              allLabel="All"
              allCount={typeMatchedAccounts.length}
              value={filter}
              onChange={(key) => setFilter(parseAccountFilter(key))}
              options={statusOptions}
            />
            <FilterDropdown
              label="Type"
              allLabel="All types"
              allCount={statusMatchedAccounts.length}
              multiple
              value={typeFilter}
              onChange={setTypeFilter}
              options={typeOptions}
            />
          </div>
        )}
        {renderBody()}
        <RecentSyncs />
      </div>
    </div>
  );
};
